/**
 * expenseCorrectionNotify — Steuerberater-Benachrichtigung nach einer erfolgreichen
 * Expense-Storno-Korrektur (architecture-expense-storno.md §6.4/§7, M-EC-6).
 *
 * v1 OHNE Messaging-Schema-Delta (kein `expenses`-Kontext-Typ — das wäre eine
 * Enum-Migration über Rules + Validatoren + Label-Map, bewusst nicht in v1):
 *   - Standardfall: `general`-Kontext mit strukturiertem Klartext.
 *   - Falls das Original aus einer Eingangsrechnung stammt
 *     (`sourceReceivedInvoiceId` gesetzt): `receipt`-Kontext mit
 *     `refCollection='received_invoices'`, `refId=sourceReceivedInvoiceId` —
 *     die Nachricht erscheint dann direkt „an der Buchung" statt im allgemeinen
 *     Thread.
 *
 * Nicht-blockierend (Bestandsmuster `writeExpenseAudit`): ein Notify-Fehler darf
 * die bereits gebuchte Korrektur NIEMALS rückgängig machen oder verstecken —
 * {@link notifyAdvisorOfExpenseCorrection} fängt jeden Fehler intern ab und
 * loggt ihn nur als Warnung.
 *
 * @module expenseCorrectionNotify
 */

import { sendMessage, MessageAuthContext } from '../api/messagesApi';
import { CreateMessageInput } from '../types/messagingTypes';
import { Expense } from '../types/expenseTypes';
import { centsToEuros } from './moneyUtils';
import logger from './logger';

/** Arguments describing the correction to notify the advisor about. */
export interface ExpenseCorrectionNotifyArgs {
  /** The original (booked) expense that was corrected. */
  original: Expense;
  /** Correction variant that was booked. */
  correctionType: 'storno' | 'privatentnahme';
  /** Whether the correction reversed input VAT (`Expense.vatReversed` of the correction doc). */
  vatReversed: boolean;
  /** The user-supplied correction reason (Belegprinzip). */
  reason: string;
  /** Firestore document ID of the newly created correction expense. */
  correctionExpenseId: string;
}

/**
 * Builds the {@link CreateMessageInput} for a correction-notification message —
 * pure (no Firebase side effect), so it is independently testable and
 * `sendMessage`'s own validation (`messagesApi.ts::validateCreateInput`) always
 * receives a structurally valid input.
 *
 * Chooses `receipt`/`received_invoices` context when the original expense has a
 * `sourceReceivedInvoiceId` (so the message threads on that incoming invoice),
 * otherwise falls back to the `general` context.
 *
 * @param args - Correction context to describe in the message.
 * @returns A structurally valid `CreateMessageInput` ready for {@link sendMessage}.
 */
export function buildExpenseCorrectionMessageInput(
  args: ExpenseCorrectionNotifyArgs,
): CreateMessageInput {
  const { original, correctionType, vatReversed, reason, correctionExpenseId } = args;

  const grossEuros = centsToEuros(Math.abs(Math.trunc(original.grossCents))).toFixed(2);
  const variantLabel =
    correctionType === 'storno' ? 'Storno (Generalumkehr)' : 'Umbuchung auf Privatentnahme';
  const belegnr =
    original.invoiceNumber && original.invoiceNumber.trim().length > 0
      ? original.invoiceNumber
      : '(ohne Belegnummer)';

  const text =
    `Ausgaben-Korrektur gebucht — Lieferant: "${original.supplierName}", Beleg: ${belegnr}, ` +
    `Betrag: ${grossEuros} EUR, Variante: ${variantLabel}, ` +
    `Vorsteuer zurückgebucht: ${vatReversed ? 'ja' : 'nein'}. ` +
    `Grund: ${reason.trim()}. ` +
    `Korrektur-Beleg-ID: ${correctionExpenseId} (Original-Beleg-ID: ${original.id}).`;

  const hasReceivedInvoiceRef =
    typeof original.sourceReceivedInvoiceId === 'string' &&
    original.sourceReceivedInvoiceId.trim().length > 0;

  if (hasReceivedInvoiceRef) {
    return {
      context: { type: 'receipt', refId: original.sourceReceivedInvoiceId as string },
      refCollection: 'received_invoices',
      text,
    };
  }

  return {
    context: { type: 'general' },
    text,
  };
}

/**
 * Sends the Steuerberater-Benachrichtigung for a booked Expense-Storno
 * correction. Non-blocking: any failure (validation, network, permission) is
 * caught and logged as a warning — it NEVER throws, so it can never roll back
 * or hide the already-booked correction (Golden Rule, mirrors
 * `expensesApi.ts::writeExpenseAudit`).
 *
 * @param args - Correction context to describe in the message.
 * @param authCtx - Deterministic author scope/uid/role (from the calling hook's `useAuth()`).
 */
export async function notifyAdvisorOfExpenseCorrection(
  args: ExpenseCorrectionNotifyArgs,
  authCtx: MessageAuthContext,
): Promise<void> {
  try {
    const input = buildExpenseCorrectionMessageInput(args);
    await sendMessage(input, authCtx);
  } catch (err) {
    logger.warn(
      'expenseCorrectionNotify: StB notification failed (non-blocking)',
      err,
    );
  }
}
